import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from '../../services/axios';
import toast from 'react-hot-toast';
import { AcademicCapIcon, UserGroupIcon, UsersIcon, PlusIcon, SparklesIcon } from '@heroicons/react/24/outline';
import PageHeader from '../../components/common/PageHeader';
import EmptyState from '../../components/common/EmptyState';
import StatusBadge from '../../components/common/StatusBadge';

function formatDate(value) {
    if (!value) return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

function DetailRow({ label, value }) {
    const display = value ?? '—';
    return (
        <div className="py-2.5 border-b border-border last:border-0">
            <div className="text-xs font-semibold uppercase tracking-wide text-ink-500">{label}</div>
            <div className="text-sm text-ink-900 mt-0.5">{display}</div>
        </div>
    );
}

function StatCard({ label, value }) {
    return (
        <div className="rounded-lg border border-border bg-ink-50/80 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-500">{label}</p>
            <p className="mt-1 text-xl font-bold text-ink-950 tabular-nums">{value}</p>
        </div>
    );
}

function userLabel(u) {
    const name = [u.first_name, u.last_name].filter(Boolean).join(' ');
    return name ? `${name} (${u.email})` : u.email;
}

function roleNames(u) {
    return (u.Roles || u.roles || []).map((r) => (typeof r === 'string' ? r : r.name));
}


function previewSplit(total, batches) {
    if (!total || !batches || batches < 1) return [];
    const n = Math.min(batches, total);
    const base = Math.floor(total / n);
    const rem = total % n;
    return Array.from({ length: n }, (_, i) => base + (i < rem ? 1 : 0));
}

const emptyAutoForm = {
    batchCount: 2,
    namePrefix: 'Auto Batch',
    program: '',
    university: '',
    start_date: '',
    end_date: '',
    hour_requirement: 300,
    status: 'active'
};

const emptyMentorForm = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: ''
};

const emptyForm = {
    name: '',
    code: '',
    program: '',
    university: '',
    start_date: '',
    end_date: '',
    hour_requirement: 300,
    max_interns: 30,
    status: 'active'
};

function CreateCohortModal({ open, onClose, onCreated }) {
    const [form, setForm] = useState(emptyForm);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) setForm(emptyForm);
    }, [open]);

    if (!open) return null;

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.start_date || !form.end_date) {
            toast.error('Name, start date, and end date are required');
            return;
        }
        setBusy(true);
        try {
            const payload = {
                ...form,
                name: form.name.trim(),
                code: form.code.trim() || null,
                program: form.program.trim() || null,
                university: form.university.trim() || null,
                hour_requirement: Number(form.hour_requirement) || 300,
                max_interns: Number(form.max_interns) || 30
            };
            const res = await axios.post('/cohorts', payload);
            toast.success('Cohort created');
            onCreated(res.data.data);
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Unable to create cohort');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink-950/40" role="dialog" aria-modal="true">
            <div className="w-full max-w-lg rounded-xl bg-white shadow-xl border border-border max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                    <h2 className="text-lg font-semibold text-ink-950">Create cohort</h2>
                    <button type="button" className="btn-ghost text-sm" onClick={onClose} disabled={busy}>
                        Close
                    </button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-4">
                    <div>
                        <label className="label" htmlFor="cohort-name">Name *</label>
                        <input id="cohort-name" className="input-field" value={form.name} onChange={set('name')} placeholder="Spring 2026 — AI Business Transformation" required />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="label" htmlFor="cohort-code">Code</label>
                            <input id="cohort-code" className="input-field" value={form.code} onChange={set('code')} placeholder="SPR-2026" />
                        </div>
                        <div>
                            <label className="label" htmlFor="cohort-status">Status</label>
                            <select id="cohort-status" className="input-field" value={form.status} onChange={set('status')}>
                                <option value="draft">Draft</option>
                                <option value="active">Active</option>
                                <option value="completed">Completed</option>
                                <option value="archived">Archived</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="label" htmlFor="cohort-program">Program</label>
                        <input id="cohort-program" className="input-field" value={form.program} onChange={set('program')} />
                    </div>
                    <div>
                        <label className="label" htmlFor="cohort-uni">University / placement</label>
                        <input id="cohort-uni" className="input-field" value={form.university} onChange={set('university')} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="label" htmlFor="cohort-start">Start date *</label>
                            <input id="cohort-start" type="date" className="input-field" value={form.start_date} onChange={set('start_date')} required />
                        </div>
                        <div>
                            <label className="label" htmlFor="cohort-end">End date *</label>
                            <input id="cohort-end" type="date" className="input-field" value={form.end_date} onChange={set('end_date')} required />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="label" htmlFor="cohort-hours">Required hours</label>
                            <input id="cohort-hours" type="number" min="0" className="input-field" value={form.hour_requirement} onChange={set('hour_requirement')} />
                        </div>
                        <div>
                            <label className="label" htmlFor="cohort-cap">Max interns</label>
                            <input id="cohort-cap" type="number" min="1" className="input-field" value={form.max_interns} onChange={set('max_interns')} />
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
                        <button type="submit" className="btn-primary" disabled={busy}>
                            {busy ? 'Creating…' : 'Create cohort'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}


function AutoSplitWizard({ open, onClose, onDone, users, onUsersChanged }) {
    const [step, setStep] = useState(1);
    const [form, setForm] = useState(emptyAutoForm);
    const [interns, setInterns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [created, setCreated] = useState([]);
    const [mentorPick, setMentorPick] = useState({});
    const [showNewMentor, setShowNewMentor] = useState(false);
    const [mentorForm, setMentorForm] = useState(emptyMentorForm);
    const [mentorTargetCohort, setMentorTargetCohort] = useState(null);

    const loadUnassigned = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get('/cohorts/unassigned-interns');
            setInterns(res.data.data?.interns || []);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Unable to load unassigned interns');
            setInterns([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!open) return;
        setStep(1);
        setForm(emptyAutoForm);
        setCreated([]);
        setMentorPick({});
        setShowNewMentor(false);
        setMentorForm(emptyMentorForm);
        setMentorTargetCohort(null);
        loadUnassigned();
    }, [open, loadUnassigned]);

    const splitPreview = useMemo(
        () => previewSplit(interns.length, Number(form.batchCount) || 0),
        [interns.length, form.batchCount]
    );

    const mentorOptions = useMemo(
        () => users.filter((u) => {
            const roles = roleNames(u);
            return roles.includes('mentor') || roles.includes('trainer');
        }),
        [users]
    );

    if (!open) return null;

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
    const setMentorField = (key) => (e) => setMentorForm((f) => ({ ...f, [key]: e.target.value }));

    const runSplit = async (e) => {
        e.preventDefault();
        const batchCount = Number(form.batchCount);
        if (!form.start_date || !form.end_date) {
            toast.error('Start and end dates are required');
            return;
        }
        if (!Number.isInteger(batchCount) || batchCount < 1) {
            toast.error('Enter how many batches to create');
            return;
        }
        if (interns.length === 0) {
            toast.error('No unassigned interns to split');
            return;
        }
        setBusy(true);
        try {
            const res = await axios.post('/cohorts/auto-split', {
                batchCount,
                namePrefix: form.namePrefix.trim() || 'Auto Batch',
                program: form.program.trim() || null,
                university: form.university.trim() || null,
                start_date: form.start_date,
                end_date: form.end_date,
                hour_requirement: Number(form.hour_requirement) || 300,
                status: form.status
            });
            const cohorts = res.data.data?.cohorts || [];
            setCreated(cohorts);
            setMentorPick(Object.fromEntries(cohorts.map((c) => [c.id, ''])));
            toast.success(res.data.message || 'Batches created');
            setStep(2);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Unable to create batches');
        } finally {
            setBusy(false);
        }
    };

    const assignMentor = async (cohortId) => {
        const userId = Number(mentorPick[cohortId]);
        if (!userId) return;
        setBusy(true);
        try {
            await axios.post('/cohorts/add-mentor', {
                cohortId,
                userId,
                isLead: false
            });
            toast.success('Mentor assigned');
            setCreated((list) => list.map((c) => {
                if (c.id !== cohortId) return c;
                const mentor = mentorOptions.find((u) => u.id === userId);
                const nextMentors = [...(c.mentors || []), mentor || { id: userId }];
                return { ...c, mentors: nextMentors, mentorCount: nextMentors.length };
            }));
            setMentorPick((m) => ({ ...m, [cohortId]: '' }));
        } catch (err) {
            toast.error(err.response?.data?.message || 'Unable to assign mentor');
        } finally {
            setBusy(false);
        }
    };

    const createMentor = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const res = await axios.post('/users', {
                firstName: mentorForm.firstName.trim(),
                lastName: mentorForm.lastName.trim(),
                email: mentorForm.email.trim(),
                phone: mentorForm.phone.trim() || undefined,
                password: mentorForm.password,
                roleName: 'mentor'
            });
            const newUser = res.data.data?.user;
            toast.success('Mentor account created');
            setMentorForm(emptyMentorForm);
            setShowNewMentor(false);
            if (onUsersChanged) await onUsersChanged();
            if (newUser && mentorTargetCohort) {
                setMentorPick((m) => ({ ...m, [mentorTargetCohort]: String(newUser.id) }));
            }
        } catch (err) {
            toast.error(err.response?.data?.message || 'Unable to create mentor');
        } finally {
            setBusy(false);
        }
    };

    const finish = async () => {
        await onDone(created[0]?.id);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink-950/40" role="dialog" aria-modal="true">
            <div className="w-full max-w-2xl rounded-xl bg-white shadow-xl border border-border max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                    <div>
                        <h2 className="text-lg font-semibold text-ink-950">
                            {step === 1 ? 'Auto split into batches' : 'Assign mentors'}
                        </h2>
                        <p className="text-xs text-ink-500 mt-0.5">
                            Step {step} of 2
                        </p>
                    </div>
                    <button type="button" className="btn-ghost text-sm" onClick={onClose} disabled={busy}>
                        Close
                    </button>
                </div>

                {step === 1 && (
                    <form onSubmit={runSplit} className="p-5 space-y-4">
                        {loading ? (
                            <div className="skeleton h-24" />
                        ) : (
                            <div className="rounded-lg border border-border bg-ink-50/80 p-4">
                                <p className="text-sm font-semibold text-ink-950">
                                    {interns.length} unassigned intern{interns.length !== 1 ? 's' : ''}
                                </p>
                                <p className="text-xs text-ink-500 mt-1">
                                    Only interns who are not in any cohort yet.
                                </p>
                                {interns.length > 0 && (
                                    <ul className="mt-3 max-h-36 overflow-y-auto text-sm text-ink-700 space-y-1">
                                        {interns.slice(0, 40).map((u) => (
                                            <li key={u.id}>{userLabel(u)}</li>
                                        ))}
                                        {interns.length > 40 && (
                                            <li className="text-ink-500">…and {interns.length - 40} more</li>
                                        )}
                                    </ul>
                                )}
                            </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="label" htmlFor="auto-batches">How many batches? *</label>
                                <input
                                    id="auto-batches"
                                    type="number"
                                    min="1"
                                    max={Math.max(interns.length, 1)}
                                    className="input-field"
                                    value={form.batchCount}
                                    onChange={set('batchCount')}
                                    required
                                />
                                {splitPreview.length > 0 && (
                                    <p className="text-xs text-ink-500 mt-1">
                                        Split preview: {splitPreview.join(' + ')} interns
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className="label" htmlFor="auto-prefix">Batch name prefix</label>
                                <input id="auto-prefix" className="input-field" value={form.namePrefix} onChange={set('namePrefix')} placeholder="Auto Batch" />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="label" htmlFor="auto-start">Start date *</label>
                                <input id="auto-start" type="date" className="input-field" value={form.start_date} onChange={set('start_date')} required />
                            </div>
                            <div>
                                <label className="label" htmlFor="auto-end">End date *</label>
                                <input id="auto-end" type="date" className="input-field" value={form.end_date} onChange={set('end_date')} required />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="label" htmlFor="auto-program">Program</label>
                                <input id="auto-program" className="input-field" value={form.program} onChange={set('program')} />
                            </div>
                            <div>
                                <label className="label" htmlFor="auto-hours">Required hours</label>
                                <input id="auto-hours" type="number" min="0" className="input-field" value={form.hour_requirement} onChange={set('hour_requirement')} />
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
                            <button
                                type="submit"
                                className="btn-primary"
                                disabled={busy || loading || interns.length === 0}
                            >
                                {busy ? 'Creating batches…' : 'Create batches & continue'}
                            </button>
                        </div>
                    </form>
                )}

                {step === 2 && (
                    <div className="p-5 space-y-4">
                        <p className="text-sm text-ink-600">
                            Batches are ready. Assign a mentor to each one — or create a new mentor below.
                        </p>

                        {created.map((cohort) => {
                            const assignedIds = new Set((cohort.mentors || []).map((m) => m.id));
                            const options = mentorOptions.filter((u) => !assignedIds.has(u.id));
                            return (
                                <section key={cohort.id} className="rounded-lg border border-border p-4 space-y-3">
                                    <div className="flex flex-wrap items-start justify-between gap-2">
                                        <div>
                                            <h3 className="text-sm font-semibold text-ink-950">{cohort.name}</h3>
                                            <p className="text-xs text-ink-500 mt-0.5">
                                                {cohort.internCount ?? cohort.interns?.length ?? 0} interns
                                                {cohort.code ? ` · ${cohort.code}` : ''}
                                            </p>
                                        </div>
                                        {(cohort.mentors?.length ?? 0) > 0 && (
                                            <span className="badge badge-success">
                                                {cohort.mentors.length} mentor{cohort.mentors.length !== 1 ? 's' : ''}
                                            </span>
                                        )}
                                    </div>

                                    {(cohort.mentors?.length ?? 0) > 0 && (
                                        <ul className="text-sm text-ink-700 space-y-1">
                                            {cohort.mentors.map((m) => (
                                                <li key={m.id}>{userLabel(m)}</li>
                                            ))}
                                        </ul>
                                    )}

                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <select
                                            className="input-field flex-1"
                                            value={mentorPick[cohort.id] || ''}
                                            onChange={(e) => setMentorPick((m) => ({ ...m, [cohort.id]: e.target.value }))}
                                            disabled={busy}
                                        >
                                            <option value="">Select mentor…</option>
                                            {options.map((u) => (
                                                <option key={u.id} value={u.id}>{userLabel(u)}</option>
                                            ))}
                                        </select>
                                        <button
                                            type="button"
                                            className="btn-primary shrink-0"
                                            disabled={busy || !mentorPick[cohort.id]}
                                            onClick={() => assignMentor(cohort.id)}
                                        >
                                            Assign
                                        </button>
                                        <button
                                            type="button"
                                            className="btn-ghost shrink-0"
                                            disabled={busy}
                                            onClick={() => {
                                                setMentorTargetCohort(cohort.id);
                                                setShowNewMentor(true);
                                            }}
                                        >
                                            + New mentor
                                        </button>
                                    </div>
                                </section>
                            );
                        })}

                        {showNewMentor && (
                            <form onSubmit={createMentor} className="rounded-lg border border-brand-200 bg-brand-50/40 p-4 space-y-3">
                                <h3 className="text-sm font-semibold text-ink-950">Create new mentor</h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="label" htmlFor="nm-first">First name *</label>
                                        <input id="nm-first" className="input-field" value={mentorForm.firstName} onChange={setMentorField('firstName')} required />
                                    </div>
                                    <div>
                                        <label className="label" htmlFor="nm-last">Last name *</label>
                                        <input id="nm-last" className="input-field" value={mentorForm.lastName} onChange={setMentorField('lastName')} required />
                                    </div>
                                </div>
                                <div>
                                    <label className="label" htmlFor="nm-email">Email *</label>
                                    <input id="nm-email" type="email" className="input-field" value={mentorForm.email} onChange={setMentorField('email')} required />
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="label" htmlFor="nm-phone">Phone</label>
                                        <input id="nm-phone" className="input-field" value={mentorForm.phone} onChange={setMentorField('phone')} />
                                    </div>
                                    <div>
                                        <label className="label" htmlFor="nm-pass">Password *</label>
                                        <input id="nm-pass" type="password" className="input-field" value={mentorForm.password} onChange={setMentorField('password')} minLength={8} required />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2">
                                    <button type="button" className="btn-ghost" onClick={() => setShowNewMentor(false)} disabled={busy}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn-primary" disabled={busy}>
                                        {busy ? 'Creating…' : 'Create mentor'}
                                    </button>
                                </div>
                            </form>
                        )}

                        <div className="flex justify-end gap-2 pt-2">
                            <button type="button" className="btn-primary" onClick={finish} disabled={busy}>
                                Done
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}


function CohortDetail({ detail, users, onRefresh }) {
    const department = detail.Department?.name || detail.department?.name;
    const [mentorId, setMentorId] = useState('');
    const [internId, setInternId] = useState('');
    const [busy, setBusy] = useState(false);

    const assignedMentorIds = useMemo(
        () => new Set((detail.mentors || []).map((m) => m.id)),
        [detail.mentors]
    );
    const assignedInternIds = useMemo(
        () => new Set((detail.interns || []).map((i) => i.id)),
        [detail.interns]
    );

    const mentorOptions = useMemo(
        () => users.filter((u) => {
            const roles = roleNames(u);
            return (roles.includes('mentor') || roles.includes('trainer')) && !assignedMentorIds.has(u.id);
        }),
        [users, assignedMentorIds]
    );

    const internOptions = useMemo(
        () => users.filter((u) => {
            const roles = roleNames(u);
            return roles.includes('intern') && !assignedInternIds.has(u.id);
        }),
        [users, assignedInternIds]
    );

    const addMentor = async () => {
        if (!mentorId) return;
        setBusy(true);
        try {
            await axios.post('/cohorts/add-mentor', {
                cohortId: detail.id,
                userId: Number(mentorId),
                isLead: false
            });
            toast.success('Mentor added to cohort');
            setMentorId('');
            onRefresh(detail.id);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Unable to add mentor');
        } finally {
            setBusy(false);
        }
    };

    const addIntern = async () => {
        if (!internId) return;
        setBusy(true);
        try {
            await axios.post('/cohorts/add-intern', {
                cohortId: detail.id,
                userId: Number(internId)
            });
            toast.success('Intern added to cohort');
            setInternId('');
            onRefresh(detail.id);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Unable to add intern');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold text-ink-950">
                        {detail.name || detail.title || `Cohort ${detail.id}`}
                    </h2>
                    {detail.code && (
                        <p className="text-sm text-ink-500 mt-0.5 font-mono">{detail.code}</p>
                    )}
                </div>
                <StatusBadge status={detail.status} />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard label="Interns" value={detail.internCount ?? detail.interns?.length ?? 0} />
                <StatCard label="Mentors" value={detail.mentorCount ?? detail.mentors?.length ?? 0} />
                <StatCard label="Required hours" value={detail.hour_requirement ?? '—'} />
                <StatCard label="Capacity" value={detail.max_interns ?? '—'} />
            </div>

            <div className="rounded-lg border border-border divide-y divide-border">
                <DetailRow label="Program" value={detail.program} />
                <DetailRow label="University / placement" value={detail.university} />
                <DetailRow label="Department" value={department} />
                <DetailRow label="Start date" value={formatDate(detail.start_date)} />
                <DetailRow label="End date" value={formatDate(detail.end_date)} />
            </div>

            <section className="rounded-lg border border-border p-4 space-y-3">
                <h3 className="text-sm font-semibold text-ink-950">Assign mentor</h3>
                <p className="text-xs text-ink-500">
                    Mentors and interns in the same cohort can see each other in the portal.
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                    <select
                        className="input-field flex-1"
                        value={mentorId}
                        onChange={(e) => setMentorId(e.target.value)}
                        disabled={busy}
                    >
                        <option value="">Select mentor…</option>
                        {mentorOptions.map((u) => (
                            <option key={u.id} value={u.id}>{userLabel(u)}</option>
                        ))}
                    </select>
                    <button type="button" className="btn-primary shrink-0" onClick={addMentor} disabled={busy || !mentorId}>
                        Add mentor
                    </button>
                </div>
                {mentorOptions.length === 0 && (
                    <p className="text-xs text-ink-500">
                        No available mentors. Create a mentor account under Staff accounts first.
                    </p>
                )}
            </section>

            <section className="rounded-lg border border-border p-4 space-y-3">
                <h3 className="text-sm font-semibold text-ink-950">Assign intern</h3>
                <div className="flex flex-col sm:flex-row gap-2">
                    <select
                        className="input-field flex-1"
                        value={internId}
                        onChange={(e) => setInternId(e.target.value)}
                        disabled={busy}
                    >
                        <option value="">Select intern…</option>
                        {internOptions.map((u) => (
                            <option key={u.id} value={u.id}>{userLabel(u)}</option>
                        ))}
                    </select>
                    <button type="button" className="btn-primary shrink-0" onClick={addIntern} disabled={busy || !internId}>
                        Add intern
                    </button>
                </div>
            </section>

            {(detail.interns?.length ?? 0) > 0 && (
                <section className="space-y-2">
                    <div className="flex items-center gap-2">
                        <UserGroupIcon className="h-4 w-4 text-ink-500" aria-hidden="true" />
                        <h3 className="text-sm font-semibold text-ink-950">Interns</h3>
                    </div>
                    <div className="overflow-x-auto rounded-lg border border-border">
                        <table className="min-w-full text-sm">
                            <thead className="bg-ink-50 text-left text-xs font-semibold uppercase tracking-wide text-ink-500">
                                <tr>
                                    <th className="px-3 py-2">Name</th>
                                    <th className="px-3 py-2">Email</th>
                                    <th className="px-3 py-2">Membership</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {detail.interns.map((intern) => (
                                    <tr key={intern.id} className="text-ink-800">
                                        <td className="px-3 py-2 font-medium">
                                            {[intern.first_name, intern.last_name].filter(Boolean).join(' ') || '—'}
                                        </td>
                                        <td className="px-3 py-2 text-ink-600">{intern.email || '—'}</td>
                                        <td className="px-3 py-2">
                                            <StatusBadge status={intern.membership_status} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {(detail.mentors?.length ?? 0) > 0 && (
                <section className="space-y-2">
                    <div className="flex items-center gap-2">
                        <UsersIcon className="h-4 w-4 text-ink-500" aria-hidden="true" />
                        <h3 className="text-sm font-semibold text-ink-950">Mentors</h3>
                    </div>
                    <div className="overflow-x-auto rounded-lg border border-border">
                        <table className="min-w-full text-sm">
                            <thead className="bg-ink-50 text-left text-xs font-semibold uppercase tracking-wide text-ink-500">
                                <tr>
                                    <th className="px-3 py-2">Name</th>
                                    <th className="px-3 py-2">Email</th>
                                    <th className="px-3 py-2">Role</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {detail.mentors.map((mentor) => (
                                    <tr key={mentor.id} className="text-ink-800">
                                        <td className="px-3 py-2 font-medium">
                                            {[mentor.first_name, mentor.last_name].filter(Boolean).join(' ') || '—'}
                                        </td>
                                        <td className="px-3 py-2 text-ink-600">{mentor.email || '—'}</td>
                                        <td className="px-3 py-2">
                                            {mentor.is_lead ? (
                                                <span className="badge badge-brand">Lead mentor</span>
                                            ) : (
                                                <span className="badge badge-neutral">Mentor</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}
        </div>
    );
}

export default function AdminCohorts() {
    const [cohorts, setCohorts] = useState([]);
    const [users, setUsers] = useState([]);
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [detailLoading, setDetailLoading] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [showAuto, setShowAuto] = useState(false);

    const loadCohorts = useCallback(async () => {
        const res = await axios.get('/cohorts');
        return res.data.data || [];
    }, []);

    const loadUsers = useCallback(async () => {
        const res = await axios.get('/users', { params: { page: 1, limit: 200 } });
        const list = res.data.data?.users || [];
        setUsers(list);
        return list;
    }, []);

    const openDetail = useCallback(async (id, list) => {
        setSelected(id);
        setDetailLoading(true);
        try {
            const res = await axios.get(`/cohorts/${id}`);
            setDetail(res.data.data);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Unable to load cohort');
            const fallback = (list || cohorts).find((c) => c.id === id);
            setDetail(fallback || null);
        } finally {
            setDetailLoading(false);
        }
    }, [cohorts]);

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const [list] = await Promise.all([
                    loadCohorts(),
                    loadUsers().catch(() => [])
                ]);
                setCohorts(list);
                if (list.length > 0) {
                    await openDetail(list[0].id, list);
                }
            } catch (err) {
                toast.error(err.response?.data?.message || 'Unable to load cohorts');
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const handleCreated = async (cohort) => {
        const list = await loadCohorts();
        setCohorts(list);
        const id = cohort?.id || list[0]?.id;
        if (id) openDetail(id, list);
    };

    const handleAutoDone = async (firstId) => {
        const list = await loadCohorts();
        setCohorts(list);
        const id = firstId || list[0]?.id;
        if (id) openDetail(id, list);
        await loadUsers().catch(() => {});
    };

    const refreshDetail = async (id) => {
        const list = await loadCohorts();
        setCohorts(list);
        await openDetail(id, list);
    };

    return (
        <div className="w-full space-y-6">
            <PageHeader
                title="Cohort Management"
                description="Create cohorts, then assign mentors and interns to the same cohort."
                actions={(
                    <div className="flex flex-wrap items-center gap-2">
                        <button type="button" className="btn-ghost" onClick={() => setShowAuto(true)}>
                            <SparklesIcon className="h-4 w-4" aria-hidden="true" />
                            Auto
                        </button>
                        <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
                            <PlusIcon className="h-4 w-4" aria-hidden="true" />
                            New cohort
                        </button>
                    </div>
                )}
            />

            <CreateCohortModal
                open={showCreate}
                onClose={() => setShowCreate(false)}
                onCreated={handleCreated}
            />

            <AutoSplitWizard
                open={showAuto}
                onClose={() => setShowAuto(false)}
                onDone={handleAutoDone}
                users={users}
                onUsersChanged={loadUsers}
            />

            {loading ? (
                <div className="space-y-3" role="status">
                    <div className="skeleton h-24" />
                    <div className="skeleton h-24" />
                    <span className="sr-only">Loading…</span>
                </div>
            ) : cohorts.length === 0 ? (
                <EmptyState
                    icon={AcademicCapIcon}
                    title="No cohorts yet"
                    description="Create your first cohort, then add mentors and interns — or use Auto to split unassigned interns."
                    action={(
                        <div className="flex flex-wrap gap-2">
                            <button type="button" className="btn-ghost" onClick={() => setShowAuto(true)}>
                                Auto split
                            </button>
                            <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
                                Create cohort
                            </button>
                        </div>
                    )}
                />
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <ul className="space-y-2">
                        {cohorts.map((c) => {
                            const counts = c.counts || {};
                            const internTotal = Number(counts.total_count)
                                || ((Number(counts.active_count) || 0)
                                    + (Number(counts.pending_count) || 0)
                                    + (Number(counts.completed_count) || 0));

                            return (
                                <li key={c.id}>
                                    <button
                                        type="button"
                                        onClick={() => openDetail(c.id)}
                                        className={`w-full text-left card transition-all ${
                                            selected === c.id
                                                ? '!border-brand-700 bg-brand-50/50'
                                                : 'hover:border-brand-200'
                                        }`}
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div className="min-w-0">
                                                <p className="font-semibold text-ink-950 truncate">
                                                    {c.name || c.title || `Cohort ${c.id}`}
                                                </p>
                                                <p className="text-xs text-ink-500 mt-1">
                                                    {c.status || 'active'}
                                                    {c.start_date ? ` · starts ${formatDate(c.start_date)}` : ''}
                                                </p>
                                                {c.program && (
                                                    <p className="text-xs text-ink-600 mt-1 truncate">{c.program}</p>
                                                )}
                                            </div>
                                            {internTotal > 0 && (
                                                <span className="badge badge-neutral shrink-0 tabular-nums">
                                                    {internTotal} intern{internTotal !== 1 ? 's' : ''}
                                                </span>
                                            )}
                                        </div>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>

                    <div className="card min-h-[200px] animate-fade-up">
                        {detailLoading ? (
                            <div className="space-y-3" role="status">
                                <div className="skeleton h-8 w-2/3" />
                                <div className="skeleton h-20" />
                                <div className="skeleton h-32" />
                                <span className="sr-only">Loading cohort details…</span>
                            </div>
                        ) : !detail ? (
                            <p className="text-sm text-ink-500">Select a cohort to view details.</p>
                        ) : (
                            <CohortDetail detail={detail} users={users} onRefresh={refreshDetail} />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
